import { Link, useLocation } from 'react-router-dom';
import { Grid, Rocket, Search, Wallet } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useCommandPalette } from './AppShell';

function isActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/' || pathname.startsWith('/token');
  return pathname === to || pathname.startsWith(`${to}/`);
}

const label = (active: boolean) =>
  cn(
    'text-3xs font-medium uppercase tracking-[0.14em] transition-colors duration-200 ease-swift',
    active ? 'text-bull' : 'text-subtle',
  );

/** Bottom tab bar. Replaces the header nav below `lg`. */
export function MobileNav() {
  const { pathname } = useLocation();
  const { openPalette } = useCommandPalette();

  const exploreActive = isActive(pathname, '/');
  const launchActive = isActive(pathname, '/launch');
  const portfolioActive = isActive(pathname, '/portfolio');

  return (
    <nav
      aria-label="Primary mobile"
      className="glass fixed inset-x-0 bottom-0 z-40 border-t border-line pb-[env(safe-area-inset-bottom)] lg:hidden"
    >
      <div className="mx-auto grid max-w-md grid-cols-4 items-end px-2 pb-1.5 pt-2">
        <Link
          to="/"
          aria-current={exploreActive ? 'page' : undefined}
          className="flex flex-col items-center gap-1 rounded-md py-1"
        >
          <Grid size={20} className={exploreActive ? 'text-bull' : 'text-muted'} aria-hidden="true" />
          <span className={label(exploreActive)}>Explore</span>
        </Link>

        {/* Launch is the primary action, so it is raised out of the bar. */}
        <Link
          to="/launch"
          aria-current={launchActive ? 'page' : undefined}
          className="flex flex-col items-center gap-1 rounded-md py-1"
        >
          <span
            className={cn(
              '-mt-6 flex h-12 w-12 items-center justify-center rounded-full bg-bull-sheen text-[rgb(var(--on-bull))] shadow-glow-bull ring-1 ring-line transition-transform duration-200 ease-swift active:scale-95',
              launchActive && 'ring-2 ring-bull',
            )}
          >
            <Rocket size={21} aria-hidden="true" />
          </span>
          <span className={label(launchActive)}>Launch</span>
        </Link>

        <Link
          to="/portfolio"
          aria-current={portfolioActive ? 'page' : undefined}
          className="flex flex-col items-center gap-1 rounded-md py-1"
        >
          <Wallet size={20} className={portfolioActive ? 'text-bull' : 'text-muted'} aria-hidden="true" />
          <span className={label(portfolioActive)}>Portfolio</span>
        </Link>

        <button
          type="button"
          onClick={openPalette}
          className="flex flex-col items-center gap-1 rounded-md py-1"
          aria-label="Search tokens"
        >
          <Search size={20} className="text-muted" aria-hidden="true" />
          <span className={label(false)}>Search</span>
        </button>
      </div>
    </nav>
  );
}

export default MobileNav;
